import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import type { TyreFormValues } from '../policies/tyre-form.policy';
import { toTyreInput } from '../policies/tyre-form.policy';
import { registerTyre, updateTyre, type TyreView } from '../tyres.api';

/** Registering and editing a tyre: a failure (a serial already in use, say) stays inline in the drawer, a saved tyre toasts. */
export function useTyreSave(afterSave: (tyre: TyreView) => Promise<unknown>) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function save(action: () => Promise<TyreView>, message: (tyre: TyreView) => string): Promise<boolean> {
    setError(null);
    setSaving(true);
    try {
      const tyre = await action();
      await afterSave(tyre);
      toast.success(message(tyre));
      return true;
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Could not save — check the fields and try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  return {
    error,
    saving,
    resetError: () => setError(null),
    register: (values: TyreFormValues) =>
      save(
        () => registerTyre(toTyreInput(values)),
        (tyre) => `Tyre ${tyre.serial} registered`,
      ),
    update: (id: string, values: TyreFormValues) =>
      save(
        () => updateTyre(id, toTyreInput(values)),
        (tyre) => `Tyre ${tyre.serial} updated`,
      ),
  };
}
